/*
 * Personal Digital Spaces LLC ("COMPANY") CONFIDENTIAL
 * Unpublished Copyright (c) 2025 Personal Digital Spaces LLC, All Rights Reserved.
 *
 * NOTICE:  All information contained herein is, and remains the property of COMPANY. The intellectual and technical concepts contained
 * herein are proprietary to COMPANY and may be covered by U.S. and Foreign Patents, patents in process, and are protected by trade secret or copyright law.
 * Dissemination of this information or reproduction of this material is strictly forbidden unless prior written permission is obtained
 * from COMPANY.  Access to the source code contained herein is hereby forbidden to anyone except current COMPANY employees, managers, contractors or partners who have executed
 * Confidentiality and Non-disclosure agreements explicitly covering such access.
 *
 * The copyright notice above does not evidence any actual or intended publication or disclosure  of  this source code, which includes
 * information that is confidential and/or proprietary, and is a trade secret, of  COMPANY.   ANY REPRODUCTION, MODIFICATION, DISTRIBUTION, PUBLIC  PERFORMANCE,
 * OR PUBLIC DISPLAY OF OR THROUGH USE  OF THIS  SOURCE CODE  WITHOUT  THE EXPRESS WRITTEN CONSENT OF COMPANY IS STRICTLY PROHIBITED, AND IN VIOLATION OF APPLICABLE
 * LAWS AND INTERNATIONAL TREATIES.  THE RECEIPT OR POSSESSION OF  THIS SOURCE CODE AND/OR RELATED INFORMATION DOES NOT CONVEY OR IMPLY ANY RIGHTS
 * TO REPRODUCE, DISCLOSE OR DISTRIBUTE ITS CONTENTS, OR TO MANUFACTURE, USE, OR SELL ANYTHING THAT IT  MAY DESCRIBE, IN WHOLE OR IN PART.
 */
import { useState, useEffect } from 'react'
import { useForm } from 'react-hook-form'
import { joiResolver } from '@hookform/resolvers/joi'

import {
  allCheckedState,
  allUncheckedState,
  calculateSelectAllState,
  getAllTagIds,
  handleCheckboxesGroupsChanges,
  handleSelectAll,
  numbersArrayToStringsArray,
  setGeographiesOrder
} from './handlers'

import { serviceGetLlmCompanies } from '@/services/llm-companies'
import { getLlmGeographies } from '@/services/llm-geographies'
import { useAuth0 } from '@auth0/auth0-react'
import { setErrorCodeAndMessage } from '@/utils/fetch'
import { validationSchema } from './build-license-listing-terms.schema'

export const useBuildLicenseTerms = ({ defaultValues = {}, onUnlist, onSubmit }) => {
  const { getAccessTokenSilently: getAccessToken } = useAuth0()
  const [hasAlert, setHasAlert] = useState(false)
  const [alert, setAlert] = useState()
  const { llmGeographiesIds, llmTagIds, llmCompanyTagIds } = defaultValues
  const [isSubmitting, setIsSubmitting] = useState(false)
  const [isLoading, setIsLoading] = useState(true)
  const [llmCompanies, setLlmCompanies] = useState()
  const [allLlmTagIds, setAlLlmTagIds] = useState([])
  const [llmGeographies, setLlmGeographies] = useState()
  const [geographiesValue, setGeographiesValue] = useState([])
  const [selectAllGeographiesState, setSelectAllGeographiesState] = useState(allUncheckedState)
  const [selectAllLlmTagsState, setSelectAllLlmTagsState] = useState(allUncheckedState)
  const [llmTagsValue, setLlmTagsValue] = useState([])
  const [selectAllLlmCompanyTagsState, setSelectAllLlmCompanyTagsState] = useState(allUncheckedState)
  const [llmCompanyTagsValue, setLlmCompanyTagsValue] = useState([])

  const hasProvidedDefaults = defaultValues && Object.keys(defaultValues).length > 0

  const {
    formState: { errors },
    handleSubmit,
    register,
    reset,
    setValue,
    watch
  } = useForm({
    resolver: joiResolver(validationSchema),
    defaultValues
  })

  const onGeographiesChange = (geoIds) => {
    setValue('llmGeographiesIds', geoIds, {
      shouldValidate: true
    })

    setGeographiesValue(geoIds)

    if (llmGeographies) {
      const newState = calculateSelectAllState(geoIds, llmGeographies.length)
      setSelectAllGeographiesState(newState)
    }
  }
  const onSelectAllGeographies = (action) => {
    const newValue = handleSelectAll({
      action,
      ids: llmGeographies,
      setSelectAllState: setSelectAllGeographiesState
    })

    setValue('llmGeographiesIds', newValue, {
      shouldValidate: true
    })

    setGeographiesValue(newValue)
  }

  const onLlmTagsChange = (models = {}) => {
    const ids = handleCheckboxesGroupsChanges('llmTagIds', models, setValue)

    if (llmCompanies.data) {
      const newState = calculateSelectAllState(ids, allLlmTagIds.length)
      setSelectAllLlmTagsState(newState)
    }
  }
  const onSelectAllLlmTags = (action) => {
    const newValue = handleSelectAll({
      action,
      ids: allLlmTagIds,
      setSelectAllState: setSelectAllLlmTagsState
    })

    setValue('llmTagIds', newValue, {
      shouldValidate: true
    })

    setLlmTagsValue(newValue)
  }

  const onLlmCompanyTagsChange = (models = {}) => {
    const ids = handleCheckboxesGroupsChanges('llmCompanyTagIds', models, setValue)

    if (llmCompanies.data) {
      const newState = calculateSelectAllState(ids, allLlmTagIds.length)
      setSelectAllLlmCompanyTagsState(newState)
    }
  }

  const onSelectAllCompanyTags = (action) => {
    const newValue = handleSelectAll({
      action,
      ids: allLlmTagIds,
      setSelectAllState: setSelectAllLlmCompanyTagsState
    })

    setValue('llmCompanyTagIds', newValue, {
      shouldValidate: true
    })

    setLlmCompanyTagsValue(newValue)
  }

  const currentOnSubmit = async (data) => {
    try {
      setIsSubmitting(true)
      onSubmit && await onSubmit(data)
    } catch (error) {
      setHasAlert(true)
      setAlert(setErrorCodeAndMessage(error))
    }

    setIsSubmitting(false)
  }

  const currentOnUnlist = async (data) => {
    try {
      setIsSubmitting(true)
      onUnlist && await onUnlist(data)
    } catch (error) {
      setHasAlert(true)
      setAlert(setErrorCodeAndMessage(error))
    }

    setIsSubmitting(false)
  }

  const fetchData = async () => {
    try {
      !isLoading && setIsLoading(true)

      const orderBy = { orderField: 'name', noLimits: true }
      const companies = await serviceGetLlmCompanies(orderBy, getAccessToken)
      const geographies = await getLlmGeographies(orderBy, getAccessToken)
      const currentGeographies = setGeographiesOrder(geographies.data)

      setLlmCompanies(companies)
      setLlmGeographies(currentGeographies)
      setAlLlmTagIds(getAllTagIds(companies.data))

      if (!hasProvidedDefaults) {
        const currentDefaultValues = {}

        if (currentGeographies.length > 0) {
          const allGeographyIds = currentGeographies.map(geo => String(geo.id))

          currentDefaultValues.llmGeographiesIds = allGeographyIds

          setGeographiesValue(allGeographyIds)
          setSelectAllGeographiesState(allCheckedState)
        }

        if (companies?.data?.length > 0) {
          const allTagIds = getAllTagIds(companies?.data).map(id => String(id))
          currentDefaultValues.llmTagIds = allTagIds
          currentDefaultValues.llmCompanyTagIds = allTagIds

          setLlmTagsValue(allTagIds)
          setSelectAllLlmTagsState(allCheckedState)
          setLlmCompanyTagsValue(allTagIds)
          setSelectAllLlmCompanyTagsState(allCheckedState)
        }

        reset({
          ...defaultValues,
          ...currentDefaultValues
        })
      } else {
        if (llmGeographiesIds) {
          setGeographiesValue(
            numbersArrayToStringsArray(llmGeographiesIds)
          )
          setSelectAllGeographiesState(
            calculateSelectAllState(
              llmGeographiesIds,
              currentGeographies.length
            )
          )
        }

        if (llmTagIds) {
          setLlmTagsValue(numbersArrayToStringsArray(llmTagIds))
          setSelectAllLlmTagsState(
            calculateSelectAllState(
              llmGeographiesIds,
              allLlmTagIds.length
            )
          )
        }

        if (llmCompanyTagIds) {
          setLlmCompanyTagsValue(numbersArrayToStringsArray(llmTagIds))
          setSelectAllLlmCompanyTagsState(
            calculateSelectAllState(
              llmCompanyTagIds,
              allLlmTagIds.length
            )
          )
        }
      }
    } catch (error) {
      console.log(error)
      setHasAlert(true)
      setAlert(setErrorCodeAndMessage(error))
    } finally {
      setIsLoading(false)
    }
  }

  useEffect(() => {
    fetchData()
  }, [])

  return {
    alert,
    currentOnSubmit,
    currentOnUnlist,
    errors,
    geographiesValue,
    handleSubmit,
    hasAlert,
    hasProvidedDefaults,
    isLoading,
    isSubmitting,
    llmCompanies,
    llmCompanyTagsValue,
    llmGeographies,
    llmTagsValue,
    onGeographiesChange,
    onLlmCompanyTagsChange,
    onLlmTagsChange,
    onSelectAllCompanyTags,
    onSelectAllGeographies,
    onSelectAllLlmTags,
    register,
    selectAllGeographiesState,
    selectAllLlmCompanyTagsState,
    selectAllLlmTagsState,
    setValue,
    watch
  }
}
